import { h, Component } from 'preact';
import { route } from 'preact-router';

import './index.less';
import { LazyLoadImage } from '../../../atoms/LazyLoadedImage';
import {
  getHashedDomainTag,
  getOpenId,
  getReferrer,
  getReferrerVal,
  getShowcaseId,
  setShowcaseId,
  setShowcaseIdSessionStorage,
  shouldRemoveCatalogueTitle,
  shouldRemoveGradient
} from '../../../lib/GroupCatalogueAmalgam';
import { getGradient, getCloudfrontUrl } from '../../../lib/GroupUtils';
import { getProduct, getProductPictureUrl } from '../../../lib/Product';
import {
  getItemsDimension,
  isResellerBundleView,
  hexToRgba,
  shouldShowLockIcon,
  shouldProcessClick,
  isNavigatingToCurrentLink,
  getPrimaryLoaderStyle,
  CHUNK_VIEW,
  isIOS,
  isMobile
} from '../../../lib/Utils';
import { onGroupCatalogueClick } from '../../../lib/Events';
import { catalogueOpened } from '../../../lib/Socket';
import {
  getCompanyId,
  getVisitorId,
  getAmalgam,
  getCatalogueTheme,
  isAmalgamFetched,
  shouldRemoveProductCountFromCatalogueTile
} from '../../../lib/Amalgam';
import { getProductImageTileSize } from '../../../lib/CompanyExperiments';
import { importSingleBundle, setLazyComponent } from '../../../lib/Route';
import I18n from '../../../lib/I18n';
import {
  GRID_VIEW_CATALOGUE_CONTAINER_HORIZONTAL_PADDING,
  GRID_VIEW_CATALOGUE_LIST_HORIZONTAL_PADDING
} from '../../../lib/PositionCache';
import LockIcon from '../../../molecules/LockIcon';

class Catalogue extends Component {
  constructor (props) {
    super(props);

    const firstProduct = getProduct(props.firstProductId, props.fromCustomPage);

    this.state = {
      fetchingAmalgam: false,
      showLoader: true,
      catalogueImage: props.pictureUrl
        ? getCloudfrontUrl(props.pictureUrl)
        : firstProduct && firstProduct.id
          ? getProductPictureUrl(firstProduct)
          : '',
      showLockIcon: shouldShowLockIcon(props.accessLevel)
    };

    this.isMounted = false;
  }

  componentDidMount () {
    this.isMounted = true;
  }

  componentWillUnmount () {
    this.isMounted = false;
  }

  route = ({ reload = false } = {}) => {
    const url = this.getCataloguePath();
    if (reload) {
      window.location.href = url;
    } else {
      route(url);
    }
  };

  onCatalogueClick = (event) => {
    const { slug, fromBundle, groupShowcaseId, fromCustomPage, id } = this.props;
    const isResellerBundle = isResellerBundleView(fromBundle);
    onGroupCatalogueClick({ callNativeHandler: isResellerBundle });
    catalogueOpened(getCompanyId(), id, getVisitorId(), getOpenId(), slug);

    if (getShowcaseId()) {
      setShowcaseIdSessionStorage(getShowcaseId());
    }

    if (groupShowcaseId && fromCustomPage) {
      setShowcaseId(groupShowcaseId);
      setShowcaseIdSessionStorage(groupShowcaseId);
    }

    if (!shouldProcessClick(event)) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if (isNavigatingToCurrentLink(this.getCataloguePath())) {
      return;
    }

    const { fetchingAmalgam } = this.state;
    if (fetchingAmalgam) {
      return;
    }

    const [companySlug, catalogueSlug, randomSlug] = slug.split('/');

    if (isAmalgamFetched({ companySlug, catalogueSlug, randomSlug })) {
      this.route();
      return;
    }

    this.setState({ fetchingAmalgam: true });

    Promise.all([
      getAmalgam({ companySlug, catalogueSlug, randomSlug, fromBundle }),
      isResellerBundle ? Promise.resolve() : importSingleBundle()
    ])
      .then(([response, module]) => {
        if (!isResellerBundle) {
          setLazyComponent(CHUNK_VIEW.SINGLE_CATALOGUE, module.default);
        }

        if (this.isMounted) {
          this.setState({ fetchingAmalgam: false });
          this.route({ reload: !!response.reloadPage });
        }
      })
      .catch(() => {
        // handled unresolved promise
      });
  };

  getCataloguePath = () => {
    const { slug, fromBundle, tabIndex, encodedDomainTag, fromCustomPage } = this.props;
    if (fromCustomPage && encodedDomainTag) {
      return `/s/${slug}?domainTag=${encodedDomainTag}`;
    }
    if (isResellerBundleView(fromBundle)) {
      return `/t/${slug}?tabIndex=${tabIndex}`;
    }

    const hashedDomainTag = getHashedDomainTag();
    const referrer = getReferrer();
    const referrerVal = getReferrerVal();

    if (hashedDomainTag) {
      return `/s/${slug}?domainTag=${hashedDomainTag}`;
    }

    if (referrer && referrerVal) {
      return `/s/${slug}?referrer=${referrer}&referrerVal=${referrerVal}`;
    }

    if (referrer) {
      return `/s/${slug}?referrer=${referrer}`;
    }

    return `/s/${slug}`;
  };

  renderGridBottomLayout () {
    const { fetchingAmalgam } = this.state;
    const {
      title,
      productCount = 0,
      hideCatalogueTitle = false,
      hideProductCount = false
    } = this.props;
    const homepageTheme = getCatalogueTheme().homepage || {};
    const primaryColor = (getCatalogueTheme().addToCartButton || {}).backgroundColor || '#3833EA';

    return (
      <div className="grid-bottom-container">
        {!shouldRemoveCatalogueTitle() && !hideCatalogueTitle && !!title && title.length > 0 && (
          <div
            className="catalogue-title-grid"
            style={{
              display: fetchingAmalgam ? 'none' : undefined,
              color: homepageTheme.catalogueTitleColor
            }}
          >
            {title}
          </div>
        )}
        {!shouldRemoveProductCountFromCatalogueTile() && !hideProductCount && productCount > 0 && (
          <div
            className="product-count product-count-grid-layout"
            style={{
              display: fetchingAmalgam ? 'none' : undefined,
              color: homepageTheme.catalogueItemCountColor
            }}
          >
            {I18n.t(productCount === 1 ? 'CATALOGUE_ITEM_COUNT' : 'CATALOGUE_ITEMS_COUNT', {
              count: productCount
            })}
          </div>
        )}
        {fetchingAmalgam && (
          <div className="fetching-amalgam-loader-grid-container">
            <div
              className="amalgam-loader-grid"
              style={{
                border: `solid 3px ${hexToRgba(primaryColor, 0.2)}`,
                borderLeft: `solid 3px ${primaryColor}`
              }}
            />
          </div>
        )}
      </div>
    );
  }

  render () {
    const {
      index,
      title,
      dominantColor,
      catalogueTileStyles = {},
      catalogueImageStyles = {},
      productImageTileSize = getProductImageTileSize()
    } = this.props;
    const { catalogueImage, showLoader, showLockIcon } = this.state;
    const homepageTheme = getCatalogueTheme().homepage || {};
    const primaryColor = (getCatalogueTheme().addToCartButton || {}).backgroundColor || '#3833EA';

    const containerStyles = {
      width: getItemsDimension(
        GRID_VIEW_CATALOGUE_LIST_HORIZONTAL_PADDING,
        GRID_VIEW_CATALOGUE_CONTAINER_HORIZONTAL_PADDING
      )
    };

    return (
      <a
        href={this.getCataloguePath()}
        onClick={this.onCatalogueClick}
        className="custom-page-catalogue-container custom-page-catalogue-container-grid"
        style={{ ...containerStyles, ...catalogueTileStyles }}
        title={title}
      >
        <div
          className="catalogue catalogue-grid"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: dominantColor ? `${dominantColor}1A` : hexToRgba(primaryColor, 0.1),
            ...catalogueImageStyles
          }}
          itemType="https://schema.org/ImageObject"
          itemScope
        >
          {!shouldRemoveGradient() && !!catalogueImage && (
            <div
              className="catalogue-gradient"
              style={{
                width: '100%',
                height: '100%',
                backgroundImage: `${getGradient(index)}, url(${catalogueImage})`,
                backgroundSize: catalogueImageStyles.objectFit || 'contain',
                ...catalogueImageStyles
              }}
            />
          )}
          <LazyLoadImage
            src={
              catalogueImage
                ? catalogueImage.replace(
                  '/products/',
                  productImageTileSize === 'FULL'
                    ? '/products/'
                    : `/products_${productImageTileSize}/`
                )
                : undefined
            }
            placeholderSrc={
              catalogueImage ? catalogueImage.replace('/products/', '/products_20/') : undefined
            }
            errorSrc={catalogueImage}
            alt={title}
            height="100%"
            width="100%"
            wrapperProps={{
              style: {
                width: '100%',
                maxWidth: '100%',
                height: '100%',
                maxHeight: '100%',
                backgroundSize: catalogueImageStyles.objectFit || 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'block',
                ...catalogueImageStyles
              }
            }}
            style={{
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              maxHeight: '100%',
              color: homepageTheme.catalogueTitleColor,
              ...catalogueImageStyles
            }}
            effect="blur"
            threshold={500}
            useIntersectionObserver
            visibleByDefault={false}
            draggable={false}
            afterPlaceholderLoad={() => {
              if (showLoader) {
                this.setState({ showLoader: false });
              }
            }}
            afterLoad={() => {
              if (showLoader) {
                this.setState({ showLoader: false });
              }
            }}
            onError={() => {
              if (showLoader) {
                this.setState({ showLoader: false });
              }
            }}
            itemProp="contentUrl"
            loading="lazy"
            decoding={isIOS() && isMobile() ? 'sync' : 'auto'}
          />
          {showLoader && (
            <div className="image-loader-container">
              <div className="image-loader" style={getPrimaryLoaderStyle()} />
            </div>
          )}
          {showLockIcon && (
            <div className="lock-icon-container">
              <LockIcon className="lock-icon" />
            </div>
          )}
        </div>
        {this.renderGridBottomLayout()}
      </a>
    );
  }
}

export default Catalogue;
